import { AxiosRequestConfig, AxiosResponse } from "axios";
import businessPortalClient from "../businessPortalClient";
import { DataTableContextFilters, DateFilter } from "../components/DataTable/DataTableContext/DataTableContextProvider";
import { Statuses } from "../components/DelegeeStatus/DelegeeStatus";
import { DelegatedEntryRightsWithPagination, EntryRightDelegeesWithPagination, EntryRightsMyFleetOverviewWithPagination } from "../models/delegations/DelegatedEntryRight";
import { WithFiltersAndPaging } from "../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../models/ForFleetManager";
import { InviteDelegeeRequestModel } from "../models/InvitePedestrianRightRequestModel";
import { ContractType, ParkingProductType } from "../models/ParkingProductModel";
import queryParam from "../utils/queryParam.utils";
import { getLocalizedDate } from "../utils/date.utils";
import { getQueryString } from "../utils/url.utils";
import { RevokeEntryRightsModel } from "../models/vehicle-assignments/RevokeEntryRightsModel";
import { RevokeEntryRightsDelegee, RevokeEntryRightsProductInfo, RevokeEntryRightsResponse } from "../models/delegations/RevokeEntryRightsResponse";
import { DeleteDelegeesModel } from "../models/vehicle-assignments/DeleteDelegeesModel";
import { appendParams, ProductContractTypeFilter } from "./common";
import { FmpFilters } from "../constants/filtering.constants";
import { StartBulkInvitationsRequestModel } from "../models/bulkImport/StartBulkInvitationsRequestModel";
import { ValidateDelegeesRequestModel } from "../models/bulkImport/ValidateDelegeesRequestModel";
import { ValidateDelegeesResponseModel } from "../models/bulkImport/ValidateDelegeesResponseModel";
import { GetBulkImportProgressResponseModel } from "../models/bulkImport/GetBulkImportProgressResponseModel";
import { GetBulkImportStatusResponseModel } from "../models/bulkImport/GetBulkImportStatusResponseModel";

const baseUrl = "/api/entryrights";
const baseUrlBulkInvitations = "/api/entryrightsbulk";

export type GetDelegatedEntryRightsQuery = {
  seasonTicketOwnerCrmId: string;
  placeId: number;
  language: string;
  pmcIds?: number[];
  name?: string;
  email?: string;
  statuses?: Statuses[];
  page: number;
  pageSize: number;
  sortBy?: 'name' | 'email' | 'invitationDate' | 'status' | 'activationDate' | 'expirationDate';
  ascending?: boolean;
  invitationFrom?: string;
  invitationTo?: string;
  activationFrom?: string;
  activationTo?: string;
  expirationFrom?: string;
  expirationTo?: string;
  contractType?: ContractType;
  productType?: ParkingProductType;
};

export function getDelegatedEntryRights(q: GetDelegatedEntryRightsQuery, config?: AxiosRequestConfig): Promise<AxiosResponse<DelegatedEntryRightsWithPagination>> {
  const { seasonTicketOwnerCrmId, language, ...others } = q;
  const qs = getQueryString(others);

  const url = `${baseUrl}/GetDelegatedEntryRights/${seasonTicketOwnerCrmId}/${language}?${qs}`
  return businessPortalClient.get(url, config);
}

export type AssignedProductInfo = {
  pmcId: number;
  entryRightId: string;
  contractId: string;
  contractType: ContractType;
  productType: ParkingProductType;
  placeId: number;
};

export type InviteEntryRightResponse = {
  delegeeId: string;
  assignedProducts: AssignedProductInfo[]
};

export function inviteDelegee(request: InviteDelegeeRequestModel): Promise<AxiosResponse<InviteEntryRightResponse>> {
  const url = `${baseUrl}/invitedelegee`
  return businessPortalClient.post(url, request);
}

export function revokeEntryRights(model: RevokeEntryRightsModel): Promise<AxiosResponse<RevokeEntryRightsResponse>> {

  type ExpectedBody = {
    customerId: string;
    delegeeRevokeModels: { delegeeId: string; entryRightIds: string[] }[]
    sendRevocationConfirmationEmail?: boolean;
  };

  const body: ExpectedBody = {
    customerId: model.customerId,
    delegeeRevokeModels: model.delegeeRevokeModels.map(delegee => ({
      delegeeId: delegee.delegeeId,
      entryRightIds: delegee.products.map(product => product.entryRightId)
    })),
    sendRevocationConfirmationEmail: model.sendRevocationConfirmationEmail
  }

  const url = `${baseUrl}/RevokeEntryRights`;
  return businessPortalClient.post(url, body);
};

export type ResendInviteModel = {
  email: string;
  customerId: string;
  entryRightId: string;
}

export type ResendAllInvitesModel = {
  email: string;
  customerId: string;
}

export function resendInvite(model: ResendInviteModel): Promise<AxiosResponse> {
  const url = `${baseUrl}/ResendInvite`;
  return businessPortalClient.post(url, model);
}

export function resendAllInvites(model: ResendAllInvitesModel): Promise<AxiosResponse> {
  const url = `${baseUrl}/ResendAllInvites`;
  return businessPortalClient.post(url, model);
}

export function deleteEntryRightDelegees(model: DeleteDelegeesModel): Promise<AxiosResponse> {
  const url = `${baseUrl}/DeleteDelegees`;
  return businessPortalClient.post(url, model);
};

export function getDelegeesMyFleet(request: WithFiltersAndPaging<ForFleetManager>, productContractTypeFilter: ProductContractTypeFilter): Promise<AxiosResponse<EntryRightDelegeesWithPagination>> {
  const url = `${baseUrl}/getDelegeesMyFleet/${request.entity.seasonTicketOwnerCrmId}/${request.entity.language}`;

  const filters: typeof request.filters = request.filters?.map((f): DataTableContextFilters => {
    if (f.key === FmpFilters.invitationDate) {
      const dateFilter = f.value as DateFilter | undefined;

      if (!dateFilter?.endDate) {
        return f;
      }
      return {
        ...f,
        value: {
          ...dateFilter,
          endDate: new Date(
            Date.UTC(
              dateFilter.endDate.getUTCFullYear(),
              dateFilter.endDate.getUTCMonth(),
              dateFilter.endDate.getUTCDate(),
              23, 59, 59, 0
            )
          )
        }
      };
    }

    return f;
  });

  const params = queryParam.buildQueryParam(
    filters!,
    request.pagination,
    (date: Date, _) => date.toISOString()
  );

  appendParams(params, productContractTypeFilter);

  return businessPortalClient.get(url, { params });
};


export function getDelegeesFlatMyFleet(
  request: WithFiltersAndPaging<ForFleetManager>,
  productContractTypeFilter: ProductContractTypeFilter
): Promise<AxiosResponse<EntryRightsMyFleetOverviewWithPagination>> {
  const url = `${baseUrl}/getDelegeesFlat/${request.entity.seasonTicketOwnerCrmId}/${request.entity.language}`;

  const params = buildMyFleetDelegeesParams(request, productContractTypeFilter);

  return businessPortalClient.get(url, { params });
}

function buildMyFleetDelegeesParams(
  request: WithFiltersAndPaging<ForFleetManager>,
  productContractTypeFilter: ProductContractTypeFilter
) {
  const normalizedFilters: typeof request.filters = request.filters?.map((f): DataTableContextFilters => {
    if (f.key === FmpFilters.invitationDate || f.key === FmpFilters.activationDate || f.key === FmpFilters.expirationDate) {
      const dateFilter = f.value as DateFilter | undefined;

      if (!dateFilter) {
        return f;
      }

      const customKeys = f.key === FmpFilters.activationDate
        ? { startKey: "activationDateStart", endKey: "activationDateEnd" }
        : f.key === FmpFilters.expirationDate
          ? { startKey: "expirationDateStart", endKey: "expirationDateEnd" }
          : dateFilter.customKeys;

      return {
        ...f,
        value: {
          ...dateFilter,
          customKeys,
        } satisfies DateFilter
      };
    }

    return f;
  });

  const params = queryParam.buildQueryParam(
    normalizedFilters!,
    request.pagination,
    (date: Date, endOfDay?: boolean) => getLocalizedDate(date, endOfDay) || date.toISOString()
  );

  appendParams(params, productContractTypeFilter);

  return params;
}

export type EntryRightDelegeeDetails = {
  id: string;
  delegeeName: string;
  delegeeEmail: string;
  products: Product[];
  status: Statuses;
}

export function getDelegeeDetails(id: string, language: string): Promise<AxiosResponse<EntryRightDelegeeDetails>> {
  const url = `${baseUrl}/GetDelegee/${id}/${language}`;
  return businessPortalClient.get(url);
}

export type UpdateEntryDelegeePayload = {
  model: UpdateEntryRightDelegeeModel,
  delegeePmcIds: number[]
}

export type Product = {
  pmcId: number,
  contractType: ContractType,
  productType: ParkingProductType,
  activationDate?: string | null,
  expirationDate?: string | null,
}

export type UpdateEntryRightDelegeeModel = {
  customerId: string;
  delegeeId: string;
  name: string;
  pmcs: Product[]
  sendRevocationConfirmationEmail?: boolean;
}

export type UpdateEntryRightResponse = {
  delegeeId: string;
  assignedPedestrianProducts: AssignedProductInfo[]
};

export function updateDelegee(model: UpdateEntryRightDelegeeModel): Promise<AxiosResponse<UpdateEntryRightResponse>> {
  const url = `${baseUrl}/UpdateDelegee`;
  return businessPortalClient.post(url, model);
}

export function startBulkInvitations(
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  request: StartBulkInvitationsRequestModel
): Promise<AxiosResponse<void>> {
  const url = `${baseUrlBulkInvitations}/BulkInvitations/${seasonTicketOwnerCrmId}/file-reference/${fileReference}`;
  return businessPortalClient.post(url, request);
}

export function validateDelegees(
  seasonTicketOwnerCrmId: string,
  request: ValidateDelegeesRequestModel
): Promise<AxiosResponse<ValidateDelegeesResponseModel>> {
  const url = `${baseUrlBulkInvitations}/ValidateDelegees/${seasonTicketOwnerCrmId}`;
  return businessPortalClient.post(url, request);
}

export function getBulkImportProgress(
  seasonTicketOwnerCrmId: string,
  fileReference: string
): Promise<AxiosResponse<GetBulkImportProgressResponseModel>> {
  const url = `${baseUrlBulkInvitations}/BulkImportProgress/${seasonTicketOwnerCrmId}/file-reference/${fileReference}`;
  return businessPortalClient.get(url);
}

export function getDelegeesValidationResult(
  customerId: string,
  fileReference: string,
  language: string
): Promise<AxiosResponse<Blob>> {
  const url = `${baseUrlBulkInvitations}/${customerId}/file-reference/${fileReference}/validationresults/${language}`;
  return businessPortalClient.get(url, {
    responseType: "blob",
  });
}

export function getDelegeesInvitationResult(
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  language: string
): Promise<AxiosResponse<Blob>> {
  const url = `${baseUrlBulkInvitations}/BulkInvitations/${seasonTicketOwnerCrmId}/file-reference/${fileReference}/invitationresults/${language}`;
  return businessPortalClient.get(url, {
    responseType: "blob",
  });
}

export function getBulkImportStatus(
  customerId: string
): Promise<AxiosResponse<GetBulkImportStatusResponseModel>> {
  const url = `${baseUrlBulkInvitations}/${customerId}/status`;
  return businessPortalClient.get(url);
}
